import React, { useEffect, useState } from 'react';
import styles from './Status.module.scss';
import { type State, type StatusIcon, type IconType, getStatusIconClass } from './status.config';
import { getStateIconPath, getStatusIconPath, analyzeIconFilesByType } from './statusIconLoader';

/**
 * Label alignment type
 */
export type LabelAlignment = 'left' | 'right';

/**
 * Props for the Status component
 */
export interface StatusProps {
  /** Current state of the status indicator */
  state?: State;
  /** Optional custom icon to override the state-based icon */
  icon?: StatusIcon | 'None';
  /** Type of icon to display */
  iconType?: IconType;
  /** Alignment of the label relative to the icon */
  labelAlignment?: LabelAlignment;
  /** Whether to show the label text */
  label?: boolean;
  /** Size of the status component */
  size?: 'Small' | 'Regular';
  /** Additional CSS classes */
  className?: string;
}

/**
 * Status component that displays a visual status indicator using icons,
 * optional text labels, and configurable alignment and size.
 *
 * ## Import path
 * ```tsx
 * import { Status } from '@kpmg-global-audit-emu/react-ui-kit/atoms/Status';
 * ```
 * @param props - Component properties
 * @param props.state - Current state of the status indicator
 * @param props.icon - Optional custom icon to override the state-based icon
 * @param props.iconType - Type of icon to display
 * @param props.labelAlignment - Alignment of the label relative to the icon
 * @param props.label - Whether to show the label text
 * @param props.size - Size of the status component
 * @param props.className - Additional CSS classes
 * @returns The rendered Status component
 */
export const Status: React.FC<StatusProps> = ({
  state = 'In Progress',
  icon,
  iconType = 'Status',
  labelAlignment = 'left',
  label = true,
  size = 'Regular',
  className = '',
}) => {
  const [iconPath, setIconPath] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Debug on mount
  useEffect(() => {
    if (import.meta.env.DEV) {
      void analyzeIconFilesByType();
    }
  }, []);

  // Load icon path
  useEffect(() => {
    const loadIcon = async () => {
      setLoading(true);
      setError(null);

      const shouldShowIcon = icon !== 'None';
      const displayIcon = shouldShowIcon ? icon : undefined;
      const displayState = state;

      let path: string | null = null;

      if (displayIcon) {
        // For custom icons

        path = getStatusIconPath(displayIcon, iconType);

        // Special handling for Status - Big
        if (!path && iconType === 'Status - Big') {
          // Try different naming variations for Status - Big
          const bigVariations = [
            // Different format variations
            { icon: displayIcon, type: 'Status-Big' },
            { icon: displayIcon, type: 'Status_Big' },
            { icon: displayIcon, type: 'StatusBig' },
            // Try with Step Completed instead of Step Complete
            ...(displayIcon === 'Step Complete'
              ? [
                { icon: 'Step Completed', type: 'Status - Big' },
                { icon: 'Step Completed', type: 'Status-Big' },
                { icon: 'Step Completed', type: 'Status_Big' },
                { icon: 'Step Completed', type: 'StatusBig' },
              ]
              : []),
            // Try without "Type=" in filename
            { icon: displayIcon, type: 'Big' },
          ];

          for (const variation of bigVariations) {
            path = getStatusIconPath(variation.icon, variation.type);
            if (path) {
              break;
            }
          }
        }

        if (!path) {
          setError(`Could not find icon "${displayIcon}" of type "${iconType}"`);
        }
      } else {
        // For default state icons
        const iconSize: 'Small' | 'Regular' = 'Regular';
        path = getStateIconPath(displayState, iconSize);
      }

      setIconPath(path);
      setLoading(false);
    };

    void loadIcon();
  }, [state, icon, iconType]);

  const shouldShowIcon = icon !== 'None';
  const displayIcon = shouldShowIcon ? icon : undefined;
  const displayState = state;
  const displayName = displayIcon ? displayIcon : displayState;
  const statusIconClass = getStatusIconClass(displayIcon || displayState);

  // Map size prop to SCSS class names
  const sizeClassName = size === 'Small' ? styles.sizeSmall : styles.sizeRegular;
  const iconBigClassName = iconType === 'Status - Big' ? styles.iconBig : '';
  const iconNavClassName = iconType === 'Nav Status' ? styles.iconNav : '';

  const statusClasses = [
    styles.status,
    sizeClassName,
    iconBigClassName,
    iconNavClassName,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Show loading state
  if (loading) {
    return (
      <div className={statusClasses}>
        <div className={styles['status-container']}>
          <span className={styles['status-text']}>Loading...</span>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && import.meta.env.DEV) {
    return (
      <div className={statusClasses}>
        <div className={styles['status-container']}>
          <span className={`${styles['status-text']} ${styles.error}`}>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={statusClasses}>
      <div className={styles['status-container']}>
        {labelAlignment === 'left' && (
          <div className={`${styles['side-slot']} ${styles.left}`}>
            {label && <span className={styles['status-text']}>{displayName}</span>}
          </div>
        )}

        <div className={styles['center-slot']}>
          {shouldShowIcon && iconPath && (
            <div className={styles['status-icon']}>
              <img
                src={iconPath}
                className={`${styles['status-svg-icon']} Icons_Background ${styles[statusIconClass]}`}
                alt={displayName}
              />
            </div>
          )}
        </div>

        {labelAlignment === 'right' && (
          <div className={`${styles['side-slot']} ${styles.right}`}>
            {label && <span className={styles['status-text']}>{displayName}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

export default Status;

